import { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useI18n } from '@/hooks/useI18n';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/useToast';
import { Skeleton } from '@/components/ui/skeleton';
import { TravelRequestsTable } from '@/lib/requests';
import { DashboardTravelRequest, IDashboardTravelRequestData } from '@/lib/models/dashboard';

interface LinkToTravelRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLink: (travelRequestId: string) => Promise<void>;
  itineraryId: string;
  itineraryTitle: string;
}

export const LinkToTravelRequestModal = ({
  isOpen,
  onClose,
  onLink,
  itineraryId,
  itineraryTitle,
}: LinkToTravelRequestModalProps) => {
  const { t } = useI18n();
  
  const [travelRequests, setTravelRequests] = useState<DashboardTravelRequest[]>([]);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLinking, setIsLinking] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    if (isOpen) {
      fetchTravelRequests();
    }
  }, [isOpen]);

  const fetchTravelRequests = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_dashboard_travel_requests', {
        p_status_exclude: null,
      });

      if (error) {
        toast({
          title: t('common.error'),
          description: t('playground.loadTravelRequestsError'),
          variant: 'destructive',
        });
        return;
      }

      // Map to DashboardTravelRequest and filter for pending/assigned only
      const mappedTravelRequests = ((data as IDashboardTravelRequestData[]) || [])
        .map((req: IDashboardTravelRequestData) => new DashboardTravelRequest(req))
        .filter((req) => req.status === 'pending' || req.status === 'assigned');

      setTravelRequests(mappedTravelRequests);
    } catch (err) {
      toast({
        title: t('common.error'),
        description: t('errors.generic'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLink = async () => {
    if (!selectedRequestId) {
      return;
    }

    setIsLinking(true);
    try {
      await onLink(selectedRequestId);
      onClose();
    } catch (error) {
      // Error handled by onLink
    } finally {
      setIsLinking(false);
    }
  };

  const handleClose = () => {
    setSelectedRequestId(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t("playground.linkToTravelRequest")}
      size="lg"
    >
      <div className="h-[80vh] flex flex-col">
        <div className="flex flex-col overflow-auto flex-shrink space-y-4 m-6 ">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              <strong>{t("playground.linkingItinerary")}:</strong>{" "}
              {itineraryTitle}
            </p>
            <p className="text-xs text-blue-700 mt-1">
              {t("playground.linkExplanation")}
            </p>
          </div>

          <div>
            <Label>{t("playground.selectTravelRequest")}</Label>

            {isLoading ? (
              <div className="mt-2">
                <Skeleton className="h-64 w-full" />
              </div>
            ) : travelRequests.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>{t("playground.noTravelRequests")}</p>
                <p className="text-sm mt-1">
                  {t("playground.noTravelRequestsDesc")}
                </p>
              </div>
            ) : (
              <div className="mt-2">
                {selectedRequestId && (
                  <div className="mb-2 text-sm text-blue-600 bg-blue-50 p-2 rounded">
                    ✓ {t("playground.selectedTravelRequest")}:{" "}
                    {travelRequests
                      .find((tr) => tr.id === selectedRequestId)
                      ?.destinations.join(", ")}
                  </div>
                )}
                <TravelRequestsTable
                  travelRequest={travelRequests}
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage}
                  userRole="travel_designer"
                  onPageChange={setCurrentPage}
                  onViewRequest={(id) => setSelectedRequestId(id)}
                  hideActions={true}
                  hideExtraInfo={true}
                  onRowClick={(id) => setSelectedRequestId(id)}
                  selectedRowId={selectedRequestId}
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-between gap-2 p-4 border-t border-gray-200">
          <Button variant="outline" onClick={handleClose} disabled={isLinking}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleLink}
            disabled={!selectedRequestId || isLinking}
          >
            {isLinking ? t("playground.linking") : t("playground.linkButton")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
